import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { styles } from "../styles/publicStyles";
import { formatDate, publishedUpdates } from "../data/updates";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EmptySection, { companyName } from "./EmptySection";
import ShowMoreButton from "./ShowMoreButton";
import { usePagedList } from "../hooks/usePagedList";
import { useFocusFlash } from "../hooks/useFocusFlash";

// `focus` changes each time the global search opens this post: it is shown
// in full, scrolled to and briefly highlighted.
const UpdatePost = ({ update, focus }) => {
  const theme = useTheme();
  const t = theme.trust;
  const [expanded, setExpanded] = useState(false);
  const { ref: postRef, highlighted } = useFocusFlash(focus);
  useEffect(() => {
    if (focus) setExpanded(true);
  }, [focus]);
  // Offer "Read more" only when two lines are not enough for the post.
  const bodyRef = useRef(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const el = bodyRef.current;
      if (el && !expanded) setClipped(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [expanded, update.body]);

  return (
    <Box
      ref={postRef}
      component="article"
      aria-label={update.title}
      aria-current={highlighted ? "true" : undefined}
      sx={{
        p: 3,
        backgroundColor: highlighted ? t.secondarySoft : "transparent",
        transition: "background-color 600ms ease",
        borderBottom: `1px solid ${theme.customColors.border.main}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      {/* Category and date together above the title, so the title and its
          text read as one block. */}
      <Stack direction="row" spacing={1.25} alignItems="center">
        {/* Only when the company gave the post a category. */}
        {update.category && (
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 500,
              px: 1,
              py: 0.25,
              borderRadius: `${t.radius.pill}px`,
              backgroundColor: t.secondarySoft,
              color: t.secondary,
            }}
          >
            {update.category}
          </Box>
        )}
        <Typography
          component="time"
          dateTime={update.publishedAt}
          variant="body2"
          sx={{ color: theme.customColors.text.secondary }}
        >
          Published {formatDate(update.publishedAt)}
        </Typography>
      </Stack>
      <Typography sx={{ fontWeight: 500, fontSize: 17, mt: 1 }}>
        {update.title}
      </Typography>
      <Typography
        ref={bodyRef}
        variant="body2"
        sx={{
          mt: 0.75,
          lineHeight: 1.7,
          whiteSpace: "pre-line",
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }),
        }}
      >
        {update.body}
      </Typography>
      {(clipped || expanded) && (
        <Link
          component="button"
          variant="body2"
          aria-label={`${expanded ? "Show less of" : "Read more about"} ${update.title}`}
          onClick={() => setExpanded((value) => !value)}
          sx={{ mt: 1 }}
        >
          {expanded ? "Show less" : "Read more"}
        </Link>
      )}
    </Box>
  );
};

// Updates tab, like Vanta's: search, then posts newest first, a page at a
// time. Every published post is already loaded, so both this search and the
// global one cover posts on later pages too.
// `focus` ({ id, at }): the post the global search picked.
const UpdatesSection = ({ updates, company, focus }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  // Nothing published: an empty-state card and no search box.
  const none = publishedUpdates(updates).length === 0;

  const matching = useMemo(() => {
    const term = search.trim().toLowerCase();
    return publishedUpdates(updates).filter(
      (u) =>
        !term ||
        u.title.toLowerCase().includes(term) ||
        u.body.toLowerCase().includes(term)
    );
  }, [updates, search]);
  const { shown, remaining, showMore, reveal, reset } = usePagedList(matching);

  // A post picked in the global search is shown even if it is on a later
  // page, or hidden by this tab's own search.
  useEffect(() => {
    if (!focus) return;
    setSearch("");
    reveal(publishedUpdates(updates).findIndex((u) => u.id === focus.id));
  }, [focus, updates, reveal]);

  return (
    <Box>
      {!none && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              reset();
            }}
            inputProps={{ "aria-label": "Search updates" }}
            sx={{ width: { xs: "100%", md: 360 }, backgroundColor: "#FFFFFF" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      )}
      {none ? (
        <EmptySection
          icon={CampaignOutlinedIcon}
          title="No updates posted yet"
          message={`${companyName(company)} has not posted any security or compliance updates yet.`}
          company={company}
        />
      ) : matching.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: theme.customColors.text.secondary }}
        >
          No updates match your search.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              ...styles.mainCard,
              boxShadow: "none",
              backgroundColor: theme.customColors.background.main,
            }}
          >
            {shown.map((update) => (
              <UpdatePost
                key={update.id}
                update={update}
                focus={focus?.id === update.id ? focus : null}
              />
            ))}
          </Box>
          <ShowMoreButton
            remaining={remaining}
            label="Show older updates"
            onClick={showMore}
          />
        </>
      )}
    </Box>
  );
};

export default UpdatesSection;
